import { TextEntity } from '@wui/im'

import { escapeHtml } from './escape-html'
import { sanitizeEntityUrl } from './sanitize-entity-url'

const openTag = (entity: TextEntity): string => {
  if (entity.type === 'bold') return '<b>'
  if (entity.type === 'italic') return '<i>'
  if (entity.type === 'underline') return '<u>'
  if (entity.type === 'strikethrough') return '<s>'
  if (entity.type === 'code') return '<code>'
  if (entity.type === 'pre') return '<pre>'
  if (entity.type === 'link') {
    const url = entity.url ? sanitizeEntityUrl(entity.url) : undefined
    if (!url) return ''
    return `<a href="${escapeHtml(url)}" rel="noopener noreferrer" target="_blank">`
  }
  if (entity.type === 'mention') {
    if (!entity.userId) return '<b>'
    return `<b data-contact-uri="${escapeHtml(entity.userId)}" contenteditable="false">`
  }
  return ''
}

const closeTag = (entity: TextEntity): string => {
  if (entity.type === 'bold' || entity.type === 'mention') return '</b>'
  if (entity.type === 'italic') return '</i>'
  if (entity.type === 'underline') return '</u>'
  if (entity.type === 'strikethrough') return '</s>'
  if (entity.type === 'code') return '</code>'
  if (entity.type === 'pre') return '</pre>'
  if (entity.type === 'link') {
    return entity.url && sanitizeEntityUrl(entity.url) ? '</a>' : ''
  }
  return ''
}

const pushAt = (map: Map<number, TextEntity[]>, index: number, entity: TextEntity) => {
  const list = map.get(index)
  if (list) {
    list.push(entity)
    return
  }
  map.set(index, [entity])
}

export const entitiesToHtml = (text: string, entities: TextEntity[] = []): string => {
  if (!text) return ''
  if (!entities.length) return escapeHtml(text)

  const byStart = new Map<number, TextEntity[]>()
  const byEnd = new Map<number, TextEntity[]>()

  for (const entity of entities) {
    const start = Math.max(0, entity.start)
    const end = Math.min(text.length, entity.end)
    if (end <= start) continue
    pushAt(byStart, start, entity)
    pushAt(byEnd, end, entity)
  }

  let html = ''
  const stack: TextEntity[] = []

  const closeEntity = (entity: TextEntity, currentIndex: number) => {
    const reopened: TextEntity[] = []
    while (stack.length) {
      const top = stack.pop()
      if (!top) break
      html += closeTag(top)
      if (top === entity) break
      reopened.push(top)
    }
    for (let i = reopened.length - 1; i >= 0; i--) {
      const nested = reopened[i]
      if (!nested || nested.end <= currentIndex) continue
      html += openTag(nested)
      stack.push(nested)
    }
  }

  for (let i = 0; i <= text.length; i++) {
    const ending = byEnd.get(i)
    if (ending?.length) {
      const ordered = [...ending].sort((a, b) => stack.lastIndexOf(b) - stack.lastIndexOf(a))
      for (const entity of ordered) {
        closeEntity(entity, i)
      }
    }

    if (i === text.length) break

    const starting = byStart.get(i)
    if (starting?.length) {
      starting.sort((a, b) => (b.end - b.start) - (a.end - a.start))
      for (const entity of starting) {
        html += openTag(entity)
        stack.push(entity)
      }
    }

    html += escapeHtml(text[i] ?? '')
  }

  while (stack.length) {
    const top = stack.pop()
    if (top) html += closeTag(top)
  }

  return html
}
